import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Globe } from "lucide-react";
import { COMPANY } from "../../data/site";
import { useLang } from "../../i18n/LanguageContext";

const NAV_KEYS = [
    { to: "/", labelKey: "nav.home" },
    {
        labelKey: "nav.gates",
        to: "/portails",
        children: [
            { to: "/portail-aluminium", labelKey: "tile.aluminium" },
            { to: "/portail-coulissant", labelKey: "tile.sliding" },
            { to: "/portail-battant", labelKey: "nav.gates" },
            { to: "/motorisation-portail", labelKey: "nav.motorization" },
        ],
    },
    { to: "/clotures", labelKey: "nav.fences" },
    { to: "/brise-vue", labelKey: "nav.privacy_screen" },
    { to: "/realisations", labelKey: "nav.projects" },
    { to: "/zones-intervention", labelKey: "nav.zones" },
    { to: "/conseils", labelKey: "nav.advice" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const { lang, setLang, t } = useLang();

    useEffect(() => { setOpen(false); }, [location.pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled ? "backdrop-blur-xl bg-[#0A1118]/90 border-b border-white/10" : "backdrop-blur-md bg-[#0A1118]/60 border-b border-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 flex items-center gap-6">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0">
                    <img
                        src={COMPANY.logo}
                        alt="Logo Portail du Léman"
                        className="w-14 h-14 md:w-16 md:h-16 object-contain group-hover:scale-105 transition-transform duration-300 drop-shadow-[0_0_18px_rgba(0,194,199,0.45)]"
                    />
                    <div className="leading-tight">
                        <div className="font-display text-2xl md:text-[1.65rem] text-white tracking-tight">Portail du Léman</div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-brand-accent/80">{t("header.tagline")}</div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-5 ml-6 text-sm whitespace-nowrap">
                    {NAV_KEYS.map((item) => (
                        item.children ? (
                            <div key={item.labelKey} className="relative group">
                                <NavLink
                                    to={item.to}
                                    data-testid={`nav-${item.labelKey}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-1 link-underline ${isActive ? "text-brand-accent" : "text-white/80 hover:text-white"}`
                                    }
                                >
                                    {t(item.labelKey)}
                                    <ChevronDown className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition" />
                                </NavLink>
                                <div className="absolute top-full left-0 pt-3 hidden group-hover:block min-w-[230px]">
                                    <div className="bg-[#0E1721] border border-white/10 shadow-2xl">
                                        {item.children.map((c) => (
                                            <Link
                                                key={c.to}
                                                to={c.to}
                                                className="block px-5 py-3 text-sm text-white/80 hover:text-brand-accent hover:bg-white/[0.03] border-b border-white/[0.04] last:border-0"
                                            >
                                                {t(c.labelKey)}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.labelKey}`}
                                className={({ isActive }) =>
                                    `link-underline ${isActive ? "text-brand-accent" : "text-white/80 hover:text-white"}`
                                }
                            >
                                {t(item.labelKey)}
                            </NavLink>
                        )
                    ))}
                </nav>

                <div className="ml-auto flex items-center gap-3">
                    {/* Language switcher */}
                    <button
                        onClick={() => setLang(lang === "fr" ? "en" : "fr")}
                        data-testid="lang-toggle"
                        className="flex items-center gap-1.5 text-xs text-white/70 hover:text-brand-accent transition border border-white/10 hover:border-brand-accent/60 px-2.5 py-1.5"
                        aria-label="Change language"
                        title={lang === "fr" ? "Switch to English" : "Passer en français"}
                    >
                        <Globe className="w-3.5 h-3.5" />
                        <span className="font-medium uppercase tracking-[0.14em]">{lang === "fr" ? "EN" : "FR"}</span>
                    </button>

                    <a
                        href={`tel:${COMPANY.phones[0].tel}`}
                        data-testid="header-call-link"
                        className="hidden md:flex items-center gap-2 text-sm text-white/80 hover:text-brand-accent transition"
                    >
                        <Phone className="w-4 h-4" />
                        <span className="font-medium">{COMPANY.phones[0].value}</span>
                    </a>
                    <Link to="/contact" data-testid="header-quote-btn" className="btn-primary text-[11px] !py-3 !px-5 hidden sm:inline-flex">
                        {t("cta.quote")}
                    </Link>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((s) => !s)}
                        className="xl:hidden text-white p-2 -mr-2"
                        aria-label="Menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div className="xl:hidden border-t border-white/10 bg-[#0A1118] max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col">
                        {NAV_KEYS.map((item) => (
                            <div key={item.labelKey} className="border-b border-white/[0.06]">
                                <NavLink
                                    to={item.to}
                                    className={({ isActive }) =>
                                        `block py-3 text-sm ${isActive ? "text-brand-accent" : "text-white/85"}`
                                    }
                                >
                                    {t(item.labelKey)}
                                </NavLink>
                                {item.children && (
                                    <div className="pb-3 pl-4 flex flex-col gap-2">
                                        {item.children.map((c) => (
                                            <Link key={c.to} to={c.to} className="text-xs text-white/60 hover:text-brand-accent">
                                                — {t(c.labelKey)}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <Link to="/contact" data-testid="mobile-quote-btn" className="btn-primary mt-5 w-full">
                            {t("cta.quote")}
                        </Link>
                        <a href={`tel:${COMPANY.phones[0].tel}`} className="btn-ghost mt-3 w-full">
                            <Phone className="w-4 h-4" /> {COMPANY.phones[0].value}
                        </a>
                        <a href={`tel:${COMPANY.phones[1].tel}`} className="btn-ghost mt-3 w-full">
                            <Phone className="w-4 h-4" /> CH {COMPANY.phones[1].value}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
